/**
 * MonthRangePicker.tsx — the shared earnings period picker (2026-08-26).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: earnings insights.
 *
 * WHY one component: the admin Insights Earnings panel and the clinician
 * "My earnings" card must offer the IDENTICAL period vocabulary — "This month
 * (to date)", the past 12 calendar months, and a custom day range — and must
 * serialise it identically (`?from=&to=`, half-open UTC instants). The
 * boundary maths is lib/monthRange.ts (pure, unit-pinned); this component is
 * only the affordance: a labelled month `<select>` plus, in custom mode, two
 * date inputs and an Apply that stays DISABLED until lib/monthRange accepts
 * the pair (never an invalid window on the wire). Kit controls with local
 * width overrides only — the kit owns focus/theming (the EarningsTab rule).
 */
import { useState } from 'react';
import {
  CUSTOM_RANGE,
  THIS_MONTH,
  customRange,
  monthOptions,
  monthRange,
  type EarningsRange,
} from '../lib/monthRange';
import { Button, Select, TextInput } from './kit';

const controlStyle = { width: 'auto', minHeight: '36px', padding: '4px 8px' } as const;
const pickerLabelStyle = { fontSize: '13px', color: 'var(--gps-muted)', fontWeight: 600 } as const;

/** The default range a mounting surface should load with: this month to date. */
export function defaultEarningsRange(now: Date): EarningsRange {
  return monthRange(THIS_MONTH, now);
}

export function MonthRangePicker({
  idBase,
  onRange,
}: {
  /** Prefix for the control ids so two pickers can share a page. */
  idBase: string;
  /** Fired with the new half-open UTC range on every committed choice. */
  onRange: (range: EarningsRange) => void;
}) {
  // The option list is fixed at mount — a picker living across a month
  // boundary keeps its labels stable rather than re-shuffling mid-session.
  const [options] = useState(() => monthOptions(new Date()));
  const [selected, setSelected] = useState<string>(THIS_MONTH);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const pendingCustom = selected === CUSTOM_RANGE ? customRange(customFrom, customTo) : null;

  function selectPeriod(id: string) {
    setSelected(id);
    // Custom mode commits on Apply only — half-typed dates never hit the wire.
    if (id !== CUSTOM_RANGE) onRange(monthRange(id, new Date()));
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--gps-s-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <label htmlFor={`${idBase}-month`} style={pickerLabelStyle}>
        Period
      </label>
      <Select id={`${idBase}-month`} value={selected} onChange={(e) => selectPeriod(e.target.value)} style={controlStyle}>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
        <option value={CUSTOM_RANGE}>Custom range</option>
      </Select>

      {selected === CUSTOM_RANGE ? (
        <>
          <label htmlFor={`${idBase}-from`} style={pickerLabelStyle}>
            From
          </label>
          <TextInput
            id={`${idBase}-from`}
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            style={controlStyle}
          />
          <label htmlFor={`${idBase}-to`} style={pickerLabelStyle}>
            To
          </label>
          <TextInput
            id={`${idBase}-to`}
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            style={controlStyle}
          />
          <Button
            variant="ghost"
            disabled={pendingCustom === null}
            onClick={() => (pendingCustom ? onRange(pendingCustom) : undefined)}
            style={{ width: 'auto', minHeight: '36px', padding: '6px 14px', fontSize: '13px' }}
          >
            Apply
          </Button>
        </>
      ) : null}
    </div>
  );
}
